import React from 'react';
import { motion } from 'framer-motion';
import { FiSearch, FiTrendingUp, FiTarget, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const SEOServicesPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 }
  };

  const services = [
    {
      id: 'traditional-seo',
      title: 'Traditional SEO',
      icon: FiSearch,
      description: 'Comprehensive on-page and off-page optimization to improve your organic search rankings.',
      features: [
        'Keyword Research & Analysis',
        'On-Page Optimization',
        'Technical SEO Audit',
        'Link Building Strategy',
        'Content Optimization',
        'Local SEO Setup'
      ],
      pricing: {
        basic: { price: '₹15,000', duration: '/month', features: ['5 Keywords', 'Monthly Report', 'Basic Support'] },
        pro: { price: '₹30,000', duration: '/month', features: ['15 Keywords', 'Weekly Reports', 'Priority Support', 'Content Creation'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Unlimited Keywords', 'Daily Reports', '24/7 Support', 'Dedicated Manager'] }
      }
    },
    {
      id: 'ai-seo',
      title: 'AI-Powered SEO',
      icon: FiTrendingUp,
      description: 'Leverage artificial intelligence for advanced keyword research, content optimization, and competitive analysis.',
      features: [
        'AI Keyword Discovery',
        'Content Gap Analysis',
        'Automated Meta Tags',
        'Smart Content Suggestions',
        'Predictive Analytics',
        'AI-Driven Competitor Analysis'
      ],
      pricing: {
        basic: { price: '₹25,000', duration: '/month', features: ['AI Analysis', 'Monthly Strategy', 'Basic Automation'] },
        pro: { price: '₹50,000', duration: '/month', features: ['Advanced AI Tools', 'Weekly Strategy', 'Full Automation', 'Priority Updates'] },
        enterprise: { price: 'Custom', duration: 'pricing', features: ['Custom AI Models', 'Real-time Analysis', 'API Access', 'White Label'] }
      }
    }
  ];

  const benefits = [
    { icon: FiTrendingUp, title: 'Increased Traffic', description: 'Drive more organic visitors to your website' },
    { icon: FiTarget, title: 'Better Rankings', description: 'Rank higher for your target keywords' },
    { icon: FiCheckCircle, title: 'ROI Growth', description: 'Achieve measurable business results' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container-width relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              SEO Services
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Dominate search results with our comprehensive SEO strategies combining traditional techniques and AI-powered insights
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-primary-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <span>Get Started</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-width">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary-100 text-primary-600 mb-6 group-hover:bg-primary-600 group-hover:text-white transition-all duration-300 transform group-hover:scale-110"
                  style={{
                    boxShadow: '0 10px 30px rgba(82, 101, 140, 0.2), inset -2px -2px 8px rgba(0, 0, 0, 0.1), inset 2px 2px 8px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <benefit.icon className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      {services.map((service, serviceIndex) => (
        <section key={service.id} className={`py-20 ${serviceIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
          <div className="container-width">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 text-white mb-6"
                style={{
                  boxShadow: '0 20px 40px rgba(82, 101, 140, 0.3), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.3)'
                }}
              >
                <service.icon className="w-12 h-12" />
              </div>
              <h2 className="text-4xl font-bold text-gray-800 mb-4">{service.title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">{service.description}</p>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
            >
              {service.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center space-x-3 p-4 rounded-xl bg-white border border-gray-200 hover:border-primary-300 hover:shadow-lg transition-all duration-300 group"
                  style={{
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05), inset -1px -1px 3px rgba(0, 0, 0, 0.05), inset 1px 1px 3px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <FiCheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="text-gray-700 font-medium">{feature}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Object.entries(service.pricing).map(([tier, details], index) => (
                <motion.div
                  key={tier}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative p-8 rounded-2xl ${
                    tier === 'pro'
                      ? 'bg-gradient-to-br from-primary-600 to-primary-800 text-white transform scale-105 shadow-2xl'
                      : 'bg-white border-2 border-gray-200 hover:border-primary-300'
                  } transition-all duration-300 group hover:shadow-xl`}
                  style={{
                    boxShadow: tier === 'pro'
                      ? '0 20px 60px rgba(82, 101, 140, 0.4), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.2)'
                      : '0 10px 30px rgba(0, 0, 0, 0.08), inset -2px -2px 6px rgba(0, 0, 0, 0.05), inset 2px 2px 6px rgba(255, 255, 255, 0.8)'
                  }}
                >
                  {tier === 'pro' && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  )}
                  
                  <h3 className={`text-2xl font-bold mb-2 capitalize ${tier === 'pro' ? 'text-white' : 'text-gray-800'}`}>
                    {tier}
                  </h3>
                  
                  <div className="mb-6">
                    <span className={`text-5xl font-bold ${tier === 'pro' ? 'text-white' : 'text-primary-600'}`}>
                      {details.price}
                    </span>
                    <span className={`text-lg ${tier === 'pro' ? 'text-white/80' : 'text-gray-500'}`}>
                      {details.duration}
                    </span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {details.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <FiCheckCircle className={`w-5 h-5 flex-shrink-0 ${tier === 'pro' ? 'text-green-300' : 'text-green-500'}`} />
                        <span className={tier === 'pro' ? 'text-white/90' : 'text-gray-600'}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className={`block w-full text-center py-3 rounded-full font-semibold transition-all duration-300 ${
                      tier === 'pro'
                        ? 'bg-white text-primary-600 hover:bg-orange-500 hover:text-white'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    Get Started
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="container-width text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Boost Your Search Rankings?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how our SEO services can help you achieve your business goals
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-primary-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <span>Book a Free Consultation</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SEOServicesPage;
